import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Clock, Plus, History, CheckCircle, XCircle, Loader2, Timer, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OvertimePage = ({ user, setUser }) => {
  const [overtimeRecords, setOvertimeRecords] = useState([]);
  const [approvedHistory, setApprovedHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  
  // Filter states for Overtime History
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  
  const [form, setForm] = useState({
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    reason: ''
  });
  
  const [calculatedOvertime, setCalculatedOvertime] = useState('00:00:00');

  // Filtered overtime records based on filters
  const filteredOvertimeRecords = useMemo(() => {
    return overtimeRecords
      .filter(r => statusFilter === 'all' || r.status === statusFilter)
      .filter(r => {
        if (monthFilter === 'all') return true;
        const date = new Date(r.start_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return monthKey === monthFilter;
      })
      .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
  }, [overtimeRecords, statusFilter, monthFilter]);

  // Get unique months for filter
  const uniqueMonths = useMemo(() => {
    const months = new Set(overtimeRecords.map(r => {
      const date = new Date(r.start_date);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }));
    return Array.from(months).sort().reverse();
  }, [overtimeRecords]);

  const getMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  };

  const calculateOvertime = useCallback(() => {
    try {
      const startDateTime = new Date(`${form.start_date}T${form.start_time}`);
      const endDateTime = new Date(`${form.end_date}T${form.end_time}`);
      
      if (endDateTime <= startDateTime) {
        setCalculatedOvertime('00:00:00');
        return;
      }
      
      const diffMs = endDateTime - startDateTime;
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      
      setCalculatedOvertime(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    } catch (error) {
      setCalculatedOvertime('00:00:00');
    }
  }, [form.start_date, form.start_time, form.end_date, form.end_time]);

  const fetchOvertimeData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [recordsRes, historyRes] = await Promise.all([
        axios.get(`${API}/overtime`, config),
        axios.get(`${API}/overtime/history`, config)
      ]);
      
      setOvertimeRecords(recordsRes.data);
      setApprovedHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching overtime data:', error);
      toast.error('Failed to load overtime data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOvertimeData();
  }, [fetchOvertimeData]);

  // Auto-calculate overtime when dates/times change
  useEffect(() => {
    if (form.start_date && form.start_time && form.end_date && form.end_time) {
      calculateOvertime();
    }
  }, [form.start_date, form.start_time, form.end_date, form.end_time, calculateOvertime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.start_date || !form.start_time || !form.end_date || !form.end_time) {
      toast.error('Please fill all date and time fields');
      return;
    }
    
    if (calculatedOvertime === '00:00:00') {
      toast.error('End date/time must be after start date/time');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        start_date: form.start_date,
        start_time: form.start_time + ':00', // Add seconds if not present
        end_date: form.end_date,
        end_time: form.end_time + ':00',
        reason: form.reason || null
      };
      
      await axios.post(`${API}/overtime`, payload, config);
      
      toast.success('Overtime submitted for approval');
      setShowDialog(false);
      setForm({
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: '',
        reason: ''
      });
      setCalculatedOvertime('00:00:00');
      fetchOvertimeData();
    } catch (error) {
      console.error('Error submitting overtime:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit overtime');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr, timeStr) => {
    return `${formatDate(dateStr)} ${timeStr}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  // Calculate total approved overtime
  const totalApprovedOvertime = () => {
    let totalSeconds = 0;
    approvedHistory.forEach(record => {
      const [hours, minutes, seconds] = record.total_overtime.split(':').map(Number);
      totalSeconds += hours * 3600 + minutes * 60 + seconds;
    });
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Header user={user} setUser={setUser} />
        <Navigation user={user} />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading overtime data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50" data-testid="overtime-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <main className="p-6">
        <div className="max-w-screen-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Overtime Portal</h1>
              <p className="text-gray-600">Submit and track your overtime hours</p>
            </div>
            
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Overtime
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Timer className="w-5 h-5 text-blue-600" />
                    Submit Overtime Request
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Start Time (HH:MM)</Label>
                      <Input
                        type="time"
                        value={form.start_time}
                        onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={form.end_date}
                        onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>End Time (HH:MM)</Label>
                      <Input
                        type="time"
                        value={form.end_time}
                        onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <Label className="text-blue-800">Total Overtime (Auto-calculated)</Label>
                    <div className="text-3xl font-bold text-blue-600 mt-1">
                      {calculatedOvertime}
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Format: HH:MM:SS</p>
                  </div>
                  
                  <div>
                    <Label>Reason (Optional)</Label>
                    <Textarea
                      value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      placeholder="Describe the reason for overtime..."
                      rows={3}
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={submitting || calculatedOvertime === '00:00:00'}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 mr-2" />
                        Submit for Approval
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-white shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Submissions</p>
                    <p className="text-2xl font-bold text-blue-600">{overtimeRecords.length}</p>
                  </div>
                  <Clock className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending Approval</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {overtimeRecords.filter(r => r.approval_status === 'pending').length}
                    </p>
                  </div>
                  <Timer className="w-10 h-10 text-amber-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Approved Hours</p>
                    <p className="text-2xl font-bold text-green-600">{totalApprovedOvertime()}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <Card className="bg-white shadow-lg">
            <CardContent className="p-0">
              <Tabs defaultValue="all" className="w-full">
                <TabsList className="w-full justify-start rounded-none border-b bg-gray-50 p-0">
                  <TabsTrigger 
                    value="all" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-white px-6 py-3"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    All Requests ({overtimeRecords.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="history"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-white px-6 py-3"
                  >
                    <History className="w-4 h-4 mr-2" />
                    Approved History ({approvedHistory.length})
                  </TabsTrigger>
                </TabsList>

                {/* All Requests Tab */}
                <TabsContent value="all" className="mt-0">
                  {/* Filters */}
                  <div className="flex gap-3 mb-4 p-3 bg-gray-50 rounded-lg flex-wrap">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Filters:</span>
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="ot-status-filter">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={monthFilter} onValueChange={setMonthFilter}>
                      <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="ot-month-filter">
                        <SelectValue placeholder="All Months" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Months</SelectItem>
                        {uniqueMonths.map(monthKey => (
                          <SelectItem key={monthKey} value={monthKey}>{getMonthLabel(monthKey)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(statusFilter !== 'all' || monthFilter !== 'all') && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setStatusFilter('all'); setMonthFilter('all'); }}
                        className="text-xs"
                      >
                        Clear
                      </Button>
                    )}
                    <span className="ml-auto text-sm text-gray-500">
                      Showing {filteredOvertimeRecords.length} of {overtimeRecords.length} records
                    </span>
                  </div>

                  {filteredOvertimeRecords.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>{overtimeRecords.length === 0 ? 'No overtime records found' : 'No matching records found'}</p>
                      <p className="text-sm mt-2">{overtimeRecords.length === 0 ? 'Click "Submit Overtime" to add your first entry' : 'Try adjusting your filters'}</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Start Date & Time</TableHead>
                          <TableHead>End Date & Time</TableHead>
                          <TableHead>Total Overtime</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Submitted</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredOvertimeRecords.map((record) => (
                          <TableRow key={record.id} className="hover:bg-gray-50">
                            <TableCell>
                              <div className="font-medium">{formatDate(record.start_date)}</div>
                              <div className="text-sm text-gray-500">{record.start_time}</div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{formatDate(record.end_date)}</div>
                              <div className="text-sm text-gray-500">{record.end_time}</div>
                            </TableCell>
                            <TableCell>
                              <span className="font-bold text-blue-600 text-lg">{record.total_overtime}</span>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate" title={record.reason}>
                              {record.reason || '-'}
                            </TableCell>
                            <TableCell>{getStatusBadge(record.approval_status)}</TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {formatDate(record.submitted_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>

                {/* Approved History Tab */}
                <TabsContent value="history" className="mt-0">
                  {approvedHistory.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No approved overtime records yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Start Date & Time</TableHead>
                          <TableHead>End Date & Time</TableHead>
                          <TableHead>Total Overtime</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Approved On</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {approvedHistory.map((record) => (
                          <TableRow key={record.id} className="hover:bg-gray-50">
                            <TableCell>
                              <div className="font-medium">{formatDate(record.start_date)}</div>
                              <div className="text-sm text-gray-500">{record.start_time}</div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{formatDate(record.end_date)}</div>
                              <div className="text-sm text-gray-500">{record.end_time}</div>
                            </TableCell>
                            <TableCell>
                              <span className="font-bold text-green-600 text-lg">{record.total_overtime}</span>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate" title={record.reason}>
                              {record.reason || '-'}
                            </TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {record.approved_at ? formatDate(record.approved_at) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default OvertimePage;
